import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  FileText,
  Upload,
  CheckCircle,
  ArrowRight,
  X,
  FolderOpen,
  ClipboardList,
  HelpCircle,
  MessageCircle,
  Rocket
} from "lucide-react";
import { Button } from "./ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

const ClientOnboardingModal = ({ isOpen, onClose, userName }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  const steps = [
    {
      id: "welcome",
      title: "¡Bienvenido a Divídelo!",
      subtitle: `Hola ${userName}, estamos emocionados de ayudarte`,
      icon: Sparkles,
      color: "emerald",
      content: (
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          <p className="text-navy-600 mb-4">
            Tu camino hacia la visa americana comienza aquí. Te guiaremos paso a paso para que el proceso sea fácil y exitoso.
          </p>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-left">
            <h4 className="font-medium text-emerald-800 mb-2">Lo que haremos juntos:</h4>
            <ul className="text-sm text-emerald-700 space-y-2">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Subir tus documentos importantes
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Completar el formulario DS-160
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Prepararte para tu entrevista
              </li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: "documents",
      title: "Paso 1: Sube tus Documentos",
      subtitle: "Necesitamos algunos documentos para empezar",
      icon: Upload,
      color: "blue",
      content: (
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <FolderOpen className="w-10 h-10 text-white" />
          </div>
          <p className="text-navy-600 mb-4">
            Primero, sube tus documentos más importantes. Esto nos ayuda a pre-llenar tu formulario.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left mb-4">
            <h4 className="font-medium text-blue-800 mb-2">Documentos recomendados:</h4>
            <ul className="text-sm text-blue-700 space-y-2">
              <li className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Pasaporte vigente (página de datos)
              </li>
              <li className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Foto tipo visa (5x5 cm, fondo blanco)
              </li>
              <li className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Comprobante de domicilio
              </li>
            </ul>
          </div>
          {progress?.documents_uploaded > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-emerald-700 flex items-center justify-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Ya tienes {progress.documents_uploaded} documento(s) subido(s)
              </p>
            </div>
          )}
        </div>
      ),
      action: {
        label: "Ir a Subir Documentos",
        icon: Upload,
        onClick: () => {
          onClose();
          navigate("/documents");
        }
      }
    },
    {
      id: "ds160",
      title: "Paso 2: Completa el DS-160",
      subtitle: "El formulario oficial de solicitud de visa",
      icon: ClipboardList,
      color: "purple",
      content: (
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <ClipboardList className="w-10 h-10 text-white" />
          </div>
          <p className="text-navy-600 mb-4">
            Ahora completa el cuestionario DS-160. Nuestro sistema te guía sección por sección.
          </p>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-left mb-4">
            <h4 className="font-medium text-purple-800 mb-2">¿Por qué es importante?</h4>
            <ul className="text-sm text-purple-700 space-y-2">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Es el formulario oficial del gobierno de EE.UU.
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Nuestro asistente te ayuda a llenarlo correctamente
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Puedes guardar y continuar en cualquier momento
              </li>
            </ul>
          </div>
          {progress?.ds160_started && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-emerald-700 flex items-center justify-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Has completado {progress.ds160_sections_completed} sección(es) del DS-160
              </p>
            </div>
          )}
        </div>
      ),
      action: {
        label: "Ir al DS-160",
        icon: ClipboardList,
        onClick: () => {
          onClose();
          navigate("/ds160");
        }
      }
    },
    {
      id: "help",
      title: "¿Necesitas Ayuda?",
      subtitle: "Estamos aquí para ti",
      icon: HelpCircle,
      color: "amber",
      content: (
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageCircle className="w-10 h-10 text-white" />
          </div>
          <p className="text-navy-600 mb-4">
            No te preocupes si tienes dudas. Tu asesor está listo para ayudarte en cualquier momento.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
            <h4 className="font-medium text-amber-800 mb-2">Formas de contactarnos:</h4>
            <ul className="text-sm text-amber-700 space-y-2">
              <li className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                Chat con tu asesor desde el sistema
              </li>
              <li className="flex items-center gap-2">
                <HelpCircle className="w-4 h-4" />
                Preguntas frecuentes en cada sección
              </li>
            </ul>
          </div>
        </div>
      )
    }
  ];

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/api/auth/onboarding-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProgress(res.data.progress);
        
        // Determine starting step based on progress
        if (res.data.progress) {
          if (res.data.progress.documents_uploaded === 0) {
            setCurrentStep(1); // Go to documents step
          } else if (!res.data.progress.ds160_started) {
            setCurrentStep(2); // Go to DS-160 step
          }
        }
      } catch (error) {
        console.error("Error fetching onboarding status:", error);
      } finally {
        setLoading(false);
      }
    };
    
    if (isOpen) {
      fetchProgress();
    }
  }, [isOpen]);

  const completeOnboarding = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/api/auth/complete-onboarding`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onClose();
    } catch (error) {
      console.error("Error completing onboarding:", error);
      onClose();
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeOnboarding();
    }
  };

  const handleSkip = () => {
    completeOnboarding();
  };

  if (!isOpen) return null;

  const step = steps[currentStep];
  const StepIcon = step.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        >
          {/* Header */}
          <div className={`px-6 py-5 bg-gradient-to-r ${
            step.color === "emerald" ? "from-emerald-500 to-green-600" :
            step.color === "blue" ? "from-blue-500 to-indigo-600" :
            step.color === "purple" ? "from-purple-500 to-indigo-600" :
            "from-amber-500 to-orange-500"
          } text-white relative`}>
            <button
              onClick={handleSkip}
              className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Progress dots */}
            <div className="flex justify-center gap-2 mb-4">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentStep ? "bg-white w-6" :
                    idx < currentStep ? "bg-white/80" : "bg-white/40"
                  }`}
                />
              ))}
            </div>
            
            <h2 className="text-xl font-bold text-center">{step.title}</h2>
            <p className="text-white/80 text-center text-sm mt-1">{step.subtitle}</p>
          </div>
          
          {/* Content */}
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-500 rounded-full animate-spin" />
              </div>
            ) : (
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {step.content}
              </motion.div>
            )}
          </div>
          
          {/* Footer */}
          <div className="px-6 pb-6 flex gap-3">
            {currentStep > 0 && (
              <Button
                variant="outline"
                onClick={() => setCurrentStep(currentStep - 1)}
                className="flex-1"
              >
                Atrás
              </Button>
            )}
            
            {step.action ? (
              <Button
                onClick={step.action.onClick}
                className={`flex-1 bg-gradient-to-r ${
                  step.color === "blue" ? "from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700" :
                  step.color === "purple" ? "from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700" :
                  "from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700"
                } text-white`}
              >
                {step.action.icon && <step.action.icon className="w-4 h-4 mr-2" />}
                {step.action.label}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                className={`flex-1 bg-gradient-to-r ${
                  step.color === "emerald" ? "from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700" :
                  step.color === "amber" ? "from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600" :
                  "from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                } text-white`}
              >
                {currentStep === steps.length - 1 ? (
                  <>
                    <Rocket className="w-4 h-4 mr-2" />
                    ¡Empezar!
                  </>
                ) : (
                  <>
                    Siguiente
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
          
          {/* Skip option */}
          <div className="px-6 pb-4 text-center">
            <button
              onClick={handleSkip}
              className="text-sm text-navy-400 hover:text-navy-600 transition-colors"
            >
              Saltar tutorial, ya sé cómo usarlo
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ClientOnboardingModal;
